import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
  withMeta,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TTypeConstructor,
  TBoolean,
  TText,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {
  TRUE,
  FALSE,
  TPredicate,
  BooleanTag,
  toBooleanTag,
  TBoolean as TUnionBoolean,
} from '@packages/systems/plugins-utils/Boolean';

import {TDynamoBoolean} from '@packages/systems/plugins-utils/DynamoGateway';

export const BooleanType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Boolean'))
  ),
});

export const dynamoBooleanToBoolean: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoBoolean)),
      TBoolean
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (x: DynamoEnvValueType<boolean>) => {
    if (x === null || x.tag === 'None') {
      return false;
    }
    return x.val;
  },
};

export const dynamoBooleanToTriStateBoolean: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoBoolean)),
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('TriStateBoolean'))
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (x: DynamoEnvValueType<boolean>) => {
    if (x === null || x.tag === 'None') {
      return 'false';
    }
    return x.val === true ? 'true' : 'false';
  },
};

export const dynamoBooleanToVisibilityConditions: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoBoolean)),
      TTypeConstructor(
        qualify(Plugins.name('Visibility'), typeName('VisibilityConditions'))
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (x: DynamoEnvValueType<boolean>) => {
    if (x === null || x.tag === 'None') {
      return [false];
    }
    return [x.val === true];
  },
};

export const dynamoBooleanToUnionBoolean: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoBoolean)),
      TUnionBoolean
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (x: DynamoEnvValueType<boolean>) => {
    if (x === null || x.tag === 'None') {
      return FALSE;
    }
    return x.val === true ? TRUE : FALSE;
  },
};

const dynamoBooleanEqualsBoolean =
  (unionBoolean: BooleanTag) => (cmsSwitch: DynamoEnvValueType<boolean>) => {
    let switchValue;
    if (cmsSwitch === null || cmsSwitch.tag === 'None') {
      switchValue = false;
    } else {
      switchValue = cmsSwitch.val;
    }
    if (unionBoolean.tag === 'True') {
      return switchValue;
    }
    return !switchValue;
  };

export const dynamoBooleanEquals: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is'},
      functionOf(
        withMeta(
          {trueLabel: 'On', falseLabel: 'Off'},
          withSystem({isBindable: true}, TUnionBoolean)
        ),
        TPredicate(TOption(TDynamoBoolean))
      )
    ),
  },
  value:
    (unionBoolean: BooleanTag) => (cmsSwitch: DynamoEnvValueType<boolean>) =>
      toBooleanTag(dynamoBooleanEqualsBoolean(unionBoolean)(cmsSwitch)),
};

export const dynamoBooleanDoesNotEqual: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is not'},
      functionOf(
        withMeta(
          {trueLabel: 'On', falseLabel: 'Off'},
          withSystem({isBindable: true}, TUnionBoolean)
        ),
        TPredicate(TOption(TDynamoBoolean))
      )
    ),
  },
  value:
    (unionBoolean: BooleanTag) => (cmsSwitch: DynamoEnvValueType<boolean>) =>
      toBooleanTag(!dynamoBooleanEqualsBoolean(unionBoolean)(cmsSwitch)),
};

export const dynamoBooleanOperand = operand(TOption(TDynamoBoolean));

export const dynamoBooleanToText: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoBoolean)),
      TText
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (x: DynamoEnvValueType<boolean>) => {
    if (x === null || x.tag === 'None') return '';
    return String(x.val);
  },
};
